import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { notification } from "antd";
import { httpService } from "../services/httpService.service";
import { editDataStore } from "../../pages/surveyBuilder/v2/surveyStore";
import { isSurveyBuilderApiEnabled } from "../../pages/surveyBuilder/v2/builderApi/featureFlag";
import {
   SurveyBuilderError,
   deleteQuestionImage
} from "../../pages/surveyBuilder/v2/builderApi/surveyBuilderApi";
import { StatusCodesEnum } from "../../utils/enums";
import { Constants } from "../../utils/constants";

export const useQuestionImage = (questionId, existingImageUrl) => {
   const { id: surveyId } = useParams();

   const [selectedImage, setSelectedImage] = useState(null);
   const addImage = editDataStore((s) => s.addImage);
   const removeImage = editDataStore((s) => s.removeImage);
   const [isDeleting, setIsDeleting] = useState(false);

   const getPreviewPhoto = useCallback(async (filePath) => {
      if (!filePath) return null;

      const correctedFilePath = filePath.replace(/\\202/g, "\\x82");
      const payload = [{ fileKey: correctedFilePath, id: filePath }];

      try {
         const response = await httpService.post(
            `/FileData/getFileUrlByFileKeyAndEntityId?platformId=${Constants.PLATFORM_ID}`,
            payload
         );

         if (response.statusCode === StatusCodesEnum.OK && response?.data?.[0]?.fileUrl) {
            return response.data[0].fileUrl;
         }
         return null;
      } catch (error) {
         console.error("Unable to fetch image preview:", error);
         return null;
      }
   }, []);

   useEffect(() => {
      if (existingImageUrl) {
         getPreviewPhoto(existingImageUrl).then((url) => {
            if (url) {
               setSelectedImage(url);
            }
         });
      }
   }, [existingImageUrl, getPreviewPhoto]);

   const handleSelectImage = (info) => {
      const file = info.file?.originFileObj;
      if (!file || file.size / 1024 / 1024 > 2) return;

      // Store the file to send it to backend later
      addImage(questionId, file);

      const reader = new FileReader();
      reader.onload = (e) => {
         setSelectedImage(e.target.result);
      };
      reader.readAsDataURL(file);
   };

   const isBase64DataURL = (str) => {
      return typeof str === "string" && str.startsWith("data:");
   };

   const handleRemoveImage = useCallback(
      async (imageSource) => {
         const isNewlyUploaded = isBase64DataURL(imageSource);

         if (!isNewlyUploaded) {
            try {
               setIsDeleting(true);

               const onDeleted = () => {
                  notification.success({ message: "Image deleted successfully" });
                  setSelectedImage("");
                  // It removes the image property from the question's entry in the editData map.
                  removeImage(questionId);
               };

               // The image is the one part of a question that was never saved with the rest of
               // it - it has had its own call all along - so it is also the one place the new API
               // is used directly rather than through a batch.
               if (isSurveyBuilderApiEnabled()) {
                  const {
                     surveyVersion,
                     surveyVersionSurveyId,
                     setSurveyVersion
                  } = editDataStore.getState();

                  const result = await deleteQuestionImage(questionId, {
                     // A version belonging to another survey is no version at all.
                     version: surveyVersionSurveyId === surveyId ? surveyVersion : null
                  });

                  setSurveyVersion(surveyId, result.surveyVersion);
                  onDeleted();
               } else {
                  await httpService.delete(
                     `/Survey/${surveyId}/Question/${questionId}/Image`,
                     null,
                     onDeleted,
                     () => {
                        notification.error({ message: "Failed to delete image" });
                     }
                  );
               }
            } catch (error) {
               notification.error({
                  message:
                     error instanceof SurveyBuilderError && error.message
                        ? error.message
                        : "Error during image API delete call"
               });
            } finally {
               setIsDeleting(false);
            }
         } else {
            setSelectedImage("");
            removeImage(questionId);
         }
      },
      [surveyId, questionId, removeImage, setSelectedImage]
   );

   return { selectedImage, isDeleting, handleSelectImage, handleRemoveImage };
};
